import React from 'react';
import { useLocation } from 'react-router-dom';

import useMountAnimation from '../hooks/useMountAnimation';
import useScrollRestoration from '../hooks/useScrollRestoration';
import useWindowScrollRestoration from './useWindowScrollRestoration';
import { videoViewFromPath } from '../pages/creatives/video-routes';
import { adViewFromPath } from '../pages/creatives/asset-routes';
import { isWebsiteTabPath } from '../pages/website/tabs';
import { isStatsTabPath } from '../pages/stats/tabs';
import { ROUTE } from '../routes';
import { PAGE_NAV } from '../theme/tokens';
import { LayerRole, NavDir, TransitionBackdropReset, TransitionHost, TransitionLayer } from './PageTransition.styles';

/**
 * A route's layer keeps `will-change: transform, opacity` at rest, a stacking context that holds every z-index in its
 * page under the layout's own chrome, which stands outside it (on a phone, the bottom navigation and the scrim over
 * the page's foot, z-index 50). A page that holds a control of its own over that chrome (Game Lists' Create a new list,
 * on a phone) asks its layer to drop it once the navigation has finished, for as long as `active` holds. Kept to the
 * pages that ask: every other page's overlays and fixed boxes were laid out inside the layer. Outside a PageTransition
 * (a CT harness) asking does nothing.
 */
const LayerHoldContext = React.createContext<(() => () => void) | null>(null);

export const useAboveAppChrome = (active: boolean): void => {
  const hold = React.useContext(LayerHoldContext);
  // Before paint, so the control is never drawn under the chrome for a frame.
  React.useLayoutEffect(() => (active && hold ? hold() : undefined), [active, hold]);
};

/**
 * One page layer. `useMountAnimation` first-paints the entering page at its
 * travel-direction offset (opacity 0), then flips to rest on the next frame so the
 * CSS transition animates it in — the same first-paint pattern the area already
 * uses for bars/gauges. The exiting layer ignores `entered` (the styles drive it
 * straight to its leaving offset), so the leaving page animates out from rest.
 *
 * The scroll-restoration hooks run against this layer's own DOM: each layer is
 * born as the entering page, so on mount each restores that history entry's saved
 * scroll (and tracks it thereafter). `useScrollRestoration` owns the desktop
 * inner-container scroll (`PageShell`); `useWindowScrollRestoration` owns the
 * mobile document scroll (where the layout makes the body the scroller so the
 * browser chrome reacts). Each is inert on the breakpoint it does not own.
 *
 * At rest the layer keeps its `will-change`, unless the page holds it open (`useAboveAppChrome`).
 */
const Layer = ({
  variant,
  dir,
  atRest,
  children,
}: {
  variant: LayerRole;
  dir: NavDir;
  /** No page is sliding out: the navigation has finished. */
  atRest: boolean;
  children: React.ReactNode;
}): React.ReactElement => {
  const entered = useMountAnimation();
  const ref = React.useRef<HTMLDivElement>(null);
  useScrollRestoration(ref);
  useWindowScrollRestoration(ref);
  const [holds, setHolds] = React.useState(0);
  const hold = React.useCallback((): (() => void) => {
    setHolds((n) => n + 1);
    return () => setHolds((n) => n - 1);
  }, []);
  return (
    <TransitionLayer
      ref={ref}
      $role={variant}
      $dir={dir}
      $entered={entered}
      // The leaving page keeps its hold as it slides out (it is the frozen instance): only the page at rest lets go.
      $settled={variant === 'enter' && atRest && holds > 0}
      aria-hidden={variant === 'exit' || undefined}
    >
      <LayerHoldContext.Provider value={hold}>{children}</LayerHoldContext.Provider>
    </TransitionLayer>
  );
};

/** React Router stores a monotonic history index in `window.history.state.idx`. */
const historyIdx = (): number => {
  const state = window.history.state as { idx?: number } | null;
  return typeof state?.idx === 'number' ? state.idx : 0;
};

// Module-level memory of the last navigation, so direction survives a *remount*.
// Some routes reuse another route's layout chrome (Settings / Pulse borrow the
// account-home page), and navigating to them can remount this point in the tree
// rather than reconcile it. A remounted component has no previous state to diff,
// so without this it could only fade. Keeping the last location key + history
// index at module scope lets a freshly-mounted instance still tell forward from
// back and slide the incoming page in from the correct side.
let lastNavKey: string | null = null;
let lastNavIdx = -1;

/** Forward vs back from a key/index pair against the last remembered navigation. */
const dirFromHistory = (locKey: string, idx: number): NavDir => {
  if (lastNavKey === null || locKey === lastNavKey) return 'none';
  return idx < lastNavIdx ? 'back' : 'forward';
};

type TransitionState = {
  /** Which of the two slots (0 | 1) holds the live, current page. */
  active: 0 | 1;
  /**
   * Birth generation per slot. A page keeps the same generation for its whole
   * on-screen life, so its layer `key` is unchanged when it flips from entering
   * to exiting — and React therefore *preserves* the instance (its real DOM, with
   * filled-in gauges/bars and the user's scroll position) instead of remounting
   * and replaying from empty.
   */
  gen: [number, number];
  /** The slot currently sliding out, and the frozen page element it renders. */
  exitingSlot: 0 | 1 | null;
  exitNode: React.ReactNode;
  /** Direction of the in-flight navigation. */
  dir: NavDir;
  /** Monotonic navigation counter; doubles as the next page's birth generation. */
  seq: number;
  /** Location identity of the live page, used to detect *any* history change. */
  locKey: string;
  /**
   * Pathname of the live page. The slide is keyed on this, not `locKey`: every
   * navigation mints a fresh key (even a query-string-only `setSearchParams`), but
   * only a change of pathname is a real *page* change worth sliding. A same-pathname
   * change (a table tab/filter, or a modal opened via `?…=`) just updates in place.
   */
  locPath: string;
  /** History index of the live page, used to derive forward vs back. */
  idx: number;
};

/**
 * Cross-page slide transition for the nova area.
 *
 * Wraps each fixed route (see `router.tsx`). On every navigation it ping-pongs
 * between two slots: the incoming page mounts into the free slot and slides in
 * from the travel direction, while the previous page stays put in its own slot
 * and slides out — as a *frozen* instance, so it keeps its exact pixels and
 * scroll rather than re-running its entrance. Direction comes from the history
 * index (PUSH → forward → from the right; POP → back → from the left), matching
 * how the pages navigate: `navigate(ROUTE.x())` to drill in, `BackHeader`'s
 * `navigate(-1)` to go back.
 *
 * When it stays mounted across a navigation (the common case — the routes share
 * one hand-written layout chrome (`../layouts/MainLayout`), so this point in the tree reconciles in
 * place) you get the full two-page slide. When a navigation remounts it instead
 * — rare now that every route shares the same `MainLayout`, which can remount this
 * subtree — there is no outgoing layer to co-mount, so it degrades to sliding the
 * incoming page in only. Direction is preserved either way via module-level
 * memory of the last navigation, so the slide never just silently fades.
 */
/** A Marketing area's route (a library or one of its editors): under the Marketing root, not the root itself, which
 *  is the hub (a trailing slash is ignored, as the router ignores it). */
const isMarketingAreaPath = (pathname: string): boolean =>
  pathname.replace(/\/+$/, '').startsWith(`${ROUTE.marketingAssets()}/`);

export const PageTransition = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const location = useLocation();

  // The live page element from the previous render — what we freeze + slide out
  // on the next navigation. Kept in a ref (it must not drive rendering).
  const prevChildrenRef = React.useRef<React.ReactNode>(children);

  const [state, setState] = React.useState<TransitionState>(() => {
    const idx = historyIdx();
    return {
      active: 0,
      gen: [0, -1],
      exitingSlot: null,
      exitNode: null,
      // If this is a fresh mount triggered by a navigation (the tree remounted
      // instead of reconciling), seed the direction from history so the incoming
      // page still slides in correctly — even though there is no outgoing layer.
      dir: dirFromHistory(location.key, idx),
      seq: 0,
      locKey: location.key,
      locPath: location.pathname,
      idx,
    };
  });

  // Detect the navigation *during render* (the React-blessed "adjust state when a
  // prop changes" pattern) so the entering and the frozen leaving page commit in
  // the same paint — no flash of the new page before the slide begins.
  //
  // `location.key` changes on EVERY history entry (true even for a query-only
  // `setSearchParams` — push and replace both go through the history's `createKey()`),
  // so it only tells us "something navigated". We slide only when the PATHNAME also
  // changed (a real page change). A same-pathname change — a Campaigns/Promotions
  // tab/filter, or an affiliate/influencer modal opened via `?…=` — updates the
  // tracked key/idx in place so the page reconciles WITHOUT a slide. Tracking `idx`
  // on every entry (both branches) keeps it current, so the next genuine page
  // navigation still derives forward vs back correctly instead of off a stale idx.
  if (location.key !== state.locKey) {
    const idx = historyIdx();
    // The Video Ads library and its EASY editor (/videos <-> /videos/:id, /videos/new) are the SAME visual
    // page: the editor is a modal over the library grid. Treat a move between them like a query-only change,
    // so opening/closing the modal does NOT re-mount + re-fetch the page (the "refresh") or slide on top of
    // the sheet's own open/close animation. Only the sheet itself animates.
    const sameVideoAdsPage =
      (videoViewFromPath(state.locPath) !== null && videoViewFromPath(location.pathname) !== null) ||
      // Same for the Banner Kits / Mini-games library and their composer (/banners <-> /banners/:id, …).
      (adViewFromPath(state.locPath) !== null && adViewFromPath(location.pathname) !== null) ||
      // The Marketing areas' routes are ONE page (one PageCreatives), so a move between them must not slide the page
      // either: that co-mounted the outgoing instance, and BOTH opened the editor sheet (it appeared to open 2-3
      // times). The Marketing root is the hub, a page of its own, so hub <-> area slides like any drill-in.
      (isMarketingAreaPath(state.locPath) && isMarketingAreaPath(location.pathname)) ||
      // The Website areas share one frame (WebsiteShell): its header (the area header, or the tab row with the
      // `website-hub` flag off) stays put while the content slides beneath it, so a move between two areas must not
      // slide the whole page, header included.
      (isWebsiteTabPath(state.locPath) && isWebsiteTabPath(location.pathname)) ||
      // The Stats page's tabs (Stats, Pulse, Website analytics) are one page too: its tab row stays put while the open
      // tab's content slides beneath it (pages/stats), so a move between two tabs must not slide the tab row with it.
      (isStatsTabPath(state.locPath) && isStatsTabPath(location.pathname));
    if (location.pathname !== state.locPath && !sameVideoAdsPage) {
      const oldActive = state.active;
      const newActive: 0 | 1 = oldActive === 0 ? 1 : 0;
      const seq = state.seq + 1;
      const gen: [number, number] = [...state.gen];
      gen[newActive] = seq; // the entering page is born this navigation
      setState({
        active: newActive,
        gen,
        exitingSlot: oldActive,
        exitNode: prevChildrenRef.current,
        dir: idx < state.idx ? 'back' : 'forward',
        seq,
        locKey: location.key,
        locPath: location.pathname,
        idx,
      });
    } else {
      // Same page, query string/hash only — no slide. Just advance the tracked
      // key + idx; the guard above (locKey now matches) clears so this can't loop.
      setState((s) => ({ ...s, locKey: location.key, idx }));
    }
  }

  // Remember the live page element so the *next* navigation can freeze it.
  React.useEffect(() => {
    prevChildrenRef.current = children;
  });

  // Keep the module-level memory current so a future remount (see above) can tell
  // which way to slide. Written after commit, read by the next instance's mount.
  React.useEffect(() => {
    lastNavKey = state.locKey;
    lastNavIdx = state.idx;
  }, [state.locKey, state.idx]);

  // Drop the outgoing layer once its exit animation has played.
  React.useEffect(() => {
    if (state.exitingSlot == null) return undefined;
    const settledSeq = state.seq;
    const timer = window.setTimeout(() => {
      setState((s) => (s.seq === settledSeq ? { ...s, exitingSlot: null, exitNode: null } : s));
    }, PAGE_NAV.DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [state.seq, state.exitingSlot]);

  const renderSlot = (slot: 0 | 1): React.ReactElement | null => {
    if (slot === state.active) {
      return (
        <Layer key={state.gen[slot]} variant="enter" dir={state.dir} atRest={state.exitingSlot == null}>
          {children}
        </Layer>
      );
    }
    if (slot === state.exitingSlot) {
      return (
        <Layer key={state.gen[slot]} variant="exit" dir={state.dir} atRest={false}>
          {state.exitNode}
        </Layer>
      );
    }
    return null;
  };

  return (
    <TransitionHost className="ai-bb-page-transition">
      <TransitionBackdropReset />
      {renderSlot(0)}
      {renderSlot(1)}
    </TransitionHost>
  );
};

export default PageTransition;
